<template>
  <cu-drawer :options="drawerOptions" @close="handleClose">
    <div class="mcp-access">
      <div v-if="loading" class="mcp-state" role="status"><el-skeleton :rows="7" animated /><p>正在加载 AI 接入设置…</p></div>
      <div v-else-if="loadError" class="mcp-state">
        <el-alert :title="loadError" type="error" :closable="false" show-icon />
        <el-button class="mcp-retry" @click="loadSettings">重新加载</el-button>
      </div>
      <template v-else-if="settings">
        <el-alert title="接入后，所选 AI 工具会处理您授权范围内的业务数据，请确认该工具可信并妥善保管连接密钥。" type="warning" :closable="false" show-icon />
        <section class="mcp-card">
          <div class="mcp-heading"><h3>我的 AI 接入</h3><el-switch :model-value="settings.enabled" :loading="saving" :disabled="busy" active-text="已开启" inactive-text="已关闭" @change="handleEnabled" /></div>
          <el-descriptions :column="1" border>
            <el-descriptions-item label="当前环境">{{ settings.environment || '服务端未提供环境，请联系维护人员确认' }}</el-descriptions-item>
            <el-descriptions-item label="MCP 服务地址"><div class="mcp-copy-row"><code>{{ settings.endpoint || '暂不可用' }}</code><el-button link type="primary" :disabled="!settings.endpoint" @click="copyText(settings.endpoint)">复制地址</el-button></div></el-descriptions-item>
            <el-descriptions-item label="身份及权限">使用当前账号身份，每次调用按账号现有权限和数据范围校验；连接不会增加账号权限。</el-descriptions-item>
            <el-descriptions-item label="数据范围">{{ scopeDescription }}</el-descriptions-item>
          </el-descriptions>
          <p class="mcp-note">总开关关闭时全部密钥暂停使用；重新开启后按各密钥原来的开关状态恢复。已启动的业务以实际处理结果为准。账号权限调整会影响后续调用。</p>
          <el-alert v-if="actionError" :title="actionError" type="error" :closable="false" show-icon />
        </section>
        <section class="mcp-card">
          <div class="mcp-heading"><h3>连接管理</h3><el-button :disabled="busy || !!oneTimeToken" @click="loadSettings">刷新</el-button></div>
          <el-form :model="form" label-width="90px" :disabled="!settings.enabled || busy" @submit.prevent="handleCreate">
              <el-form-item label="连接名称" required><el-input v-model="form.name" maxlength="60" placeholder="例如：我的 AI 客户端" /></el-form-item>
            <el-form-item label="连接模式" required><el-radio-group v-model="form.mode"><el-radio label="read_write">按本人权限读写</el-radio><el-radio label="read_only">只读</el-radio></el-radio-group></el-form-item>
            <el-form-item><el-button type="primary" :loading="creating" :disabled="!settings.enabled || busy || !!oneTimeToken" @click="handleCreate">创建连接</el-button></el-form-item>
          </el-form>
          <p class="mcp-note">名称仅用于本人识别连接，不代表已验证客户端身份。只读连接不能执行写入；读写连接仍受本人业务权限限制。密钥长期有效，创建后默认开启，可在列表中单独关闭或开启。</p>
          <div v-if="oneTimeToken" class="mcp-secret" role="status">
            <h4>连接已创建 · 密钥仅显示这一次</h4>
            <p>请立即复制并存入可信客户端。关闭此结果或抽屉后无法再次查看，遗失时请撤销后重新创建。</p>
            <div class="mcp-copy-row"><el-input :model-value="oneTimeToken" type="password" show-password readonly autocomplete="off" /><el-button @click="copyText(oneTimeToken)">复制密钥</el-button></div>
            <pre>{{ oneTimeConfig }}</pre>
            <div class="mcp-secret-actions"><el-button type="primary" @click="copyText(oneTimeConfig)">复制配置</el-button><el-button @click="clearOneTimeToken">已保存，关闭结果</el-button></div>
          </div>
          <el-table :data="settings.connections || []" border empty-text="暂无连接，开启后可创建" class="mcp-table">
            <el-table-column prop="name" label="连接名称" min-width="150" show-overflow-tooltip />
            <el-table-column label="凭证标识" width="145"><template #default="{ row }">{{ row.token_prefix ? `${String(row.token_prefix).slice(0, 16)}…` : '—' }}</template></el-table-column>
            <el-table-column label="模式" width="110"><template #default="{ row }">{{ row.mode === 'read_only' ? '只读' : '按权限读写' }}</template></el-table-column>
            <el-table-column label="状态" width="95"><template #default="{ row }"><el-tag :type="connectionStatus(row).type">{{ connectionStatus(row).label }}</el-tag></template></el-table-column>
            <el-table-column label="最近使用" min-width="175"><template #default="{ row }">{{ formatAccessTime(row.last_used_at) }}</template></el-table-column>
            <el-table-column label="操作" width="150" fixed="right"><template #default="{ row }"><div class="mcp-row-actions">
              <el-button v-if="connectionStatus(row).toggleable" link type="primary" :disabled="busy" :loading="togglingId === row.connection_id" @click="handleConnectionEnabled(row)">{{ row.enabled ? '关闭' : '开启' }}</el-button>
              <el-button v-if="connectionStatus(row).label !== '已撤销'" link type="danger" :disabled="busy" :loading="revokingId === row.connection_id" @click="handleRevoke(row)">撤销</el-button><span v-else>—</span>
            </div></template></el-table-column>
          </el-table>
        </section>
        <section class="mcp-card">
          <h3>HTTP 客户端配置参考</h3>
          <p class="mcp-note">支持 HTTP 和 Bearer 认证的 AI 客户端可参考以下配置，具体字段以客户端文档为准。</p>
          <pre>{{ exampleConfig }}</pre>
          <el-button :disabled="!settings.endpoint" @click="copyText(exampleConfig)">复制配置模板</el-button>
        </section>
        <section class="mcp-card">
          <div class="mcp-heading"><h3>本人最近调用</h3><el-button :loading="logsLoading" :disabled="logsLoading" @click="loadLogs">刷新记录</el-button></div>
          <div v-if="logsError"><el-alert :title="logsError" type="error" :closable="false" show-icon /><el-button class="mcp-retry" @click="loadLogs">重试</el-button></div>
          <el-table v-else v-loading="logsLoading" :data="logs" border empty-text="暂无调用记录">
            <el-table-column prop="tool" label="工具" min-width="180" show-overflow-tooltip />
            <el-table-column prop="status" label="结果" width="110" />
            <el-table-column label="结果摘要" min-width="230" show-overflow-tooltip><template #default="{ row }">{{ safeAccessMessage(row.message) || '—' }}</template></el-table-column>
            <el-table-column label="调用时间" min-width="175"><template #default="{ row }">{{ formatAccessTime(row.create_time) }}</template></el-table-column>
            <el-table-column label="耗时(ms)" width="110"><template #default="{ row }">{{ row.duration_ms ?? '—' }}</template></el-table-column>
          </el-table>
          <el-pagination v-if="logsTotal > logQuery.row" class="mcp-pagination" small layout="prev, pager, next, total" :current-page="logQuery.page" :page-size="logQuery.row" :total="logsTotal" @current-change="changeLogPage" />
        </section>
      </template>
    </div>
  </cu-drawer>
</template>

<script setup>
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'
import CuDrawer from '@/components/common/CuDrawer.vue'
import api from '@/api/index.js'
import { message } from '@/utils/common'
import { clientConfig, connectionPayload, connectionStatus, formatAccessTime, safeAccessMessage } from '@/utils/mcpAccess'

const props = defineProps({ modelValue: { type: Boolean, default: false } })
const emit = defineEmits(['update:modelValue'])
const settings = ref(null)
const loading = ref(false)
const saving = ref(false)
const creating = ref(false)
const revokingId = ref(null)
const togglingId = ref(null)
const loadError = ref('')
const actionError = ref('')
const oneTimeToken = ref('')
const logs = ref([])
const logsLoading = ref(false)
const logsError = ref('')
const logsTotal = ref(0)
const logQuery = reactive({ page: 1, row: 10 })
const form = reactive({ name: '', mode: 'read_write' })
let session = 0
let logRequest = 0
const busy = computed(() => saving.value || creating.value || revokingId.value !== null || togglingId.value !== null)
const drawerOptions = computed(() => ({ show: props.modelValue, title: 'AI 接入设置', width: 'min(1120px, 96vw)', button: false }))
const exampleConfig = computed(() => clientConfig(settings.value?.endpoint || ''))
const oneTimeConfig = computed(() => oneTimeToken.value ? clientConfig(settings.value?.endpoint || '', oneTimeToken.value) : '')
const scopeDescription = computed(() => {
  const scope = settings.value?.scope
  if (!scope) return '以当前账号服务端校验的数据范围为准'
  const labels = { platform: '平台授权范围', company: '本人所属企业', operation: '本人运营中心及下级授权范围', personal: '本人业务范围' }
  const text = labels[scope.mode] || '当前账号授权范围'
  return Array.isArray(scope.company_ids) && scope.company_ids.length ? `${text}（企业 ID：${scope.company_ids.join('、')}）` : `${text}，以每次调用的实时校验为准`
})
const errorText = error => safeAccessMessage(error?.message) || '操作失败，请稍后重试'
const isCurrent = current => current === session && props.modelValue
const clearOneTimeToken = () => { oneTimeToken.value = '' }

const loadSettings = async () => {
  if (busy.value) return
  const current = ++session
  loading.value = true
  loadError.value = ''
  actionError.value = ''
  clearOneTimeToken()
  try {
    const data = await api.mcpAccess.settings()
    if (!isCurrent(current)) return
    if (!data || typeof data.enabled !== 'boolean') throw new Error('接入设置数据不完整，请重试')
    settings.value = data
    loadLogs()
  } catch (error) {
    if (isCurrent(current)) { settings.value = null; loadError.value = errorText(error) }
  } finally { if (isCurrent(current)) loading.value = false }
}

const loadLogs = async () => {
  const current = session
  const requestId = ++logRequest
  logsLoading.value = true
  logsError.value = ''
  try {
    const data = await api.mcpAccess.logs({ ...logQuery })
    if (!isCurrent(current) || requestId !== logRequest) return
    logs.value = Array.isArray(data?.list) ? data.list.map(row => ({
      tool: safeAccessMessage(row.tool), status: safeAccessMessage(row.status),
      message: safeAccessMessage(row.message), create_time: row.create_time, duration_ms: row.duration_ms
    })) : []
    logsTotal.value = Number(data?.total) || 0
  } catch (error) {
    if (isCurrent(current) && requestId === logRequest) { logs.value = []; logsError.value = errorText(error) }
  } finally { if (isCurrent(current) && requestId === logRequest) logsLoading.value = false }
}

const handleEnabled = async enabled => {
  if (busy.value || !settings.value) return
  const current = session
  saving.value = true
  actionError.value = ''
  try {
    if (!enabled && !await message.confirm('关闭 AI 接入将暂停全部密钥。重新开启后按各密钥原来的开关状态恢复。确定关闭？')) return
    if (!isCurrent(current)) return
    const data = await api.mcpAccess.setEnabled(enabled)
    if (!isCurrent(current)) return
    if (!data || typeof data.enabled !== 'boolean') throw new Error('开关保存结果不完整，请刷新确认')
    settings.value = data
    clearOneTimeToken()
    if (data.enabled !== enabled) throw new Error('开关状态未保存成功，请刷新设置后重试')
    message.success(data.enabled ? 'AI 接入已开启' : 'AI 接入已关闭，全部密钥已暂停')
  } catch (error) { if (isCurrent(current)) actionError.value = errorText(error) }
  finally { if (isCurrent(current)) saving.value = false }
}

const handleCreate = async () => {
  if (busy.value || !settings.value?.enabled || oneTimeToken.value) return
  const current = session
  creating.value = true
  actionError.value = ''
  try {
    const payload = connectionPayload(form)
    const data = await api.mcpAccess.createConnection(payload)
    if (!isCurrent(current)) return
    if (!data?.token || !data?.connection || data.connection.enabled !== true || data.connection.expires_at !== null) throw new Error('连接创建结果不完整，请刷新列表确认并撤销无密钥的连接')
    oneTimeToken.value = data.token
    settings.value.connections = [data.connection, ...(settings.value.connections || [])]
    form.name = ''
    message.success('连接已创建，请立即保存密钥')
  } catch (error) { if (isCurrent(current)) actionError.value = errorText(error) }
  finally { if (isCurrent(current)) creating.value = false }
}

const handleConnectionEnabled = async connection => {
  if (busy.value || !connectionStatus(connection).toggleable) return
  const current = session
  const enabled = !connection.enabled
  togglingId.value = connection.connection_id
  actionError.value = ''
  try {
    const data = await api.mcpAccess.setConnectionEnabled(connection.connection_id, enabled)
    if (!isCurrent(current)) return
    const saved = data?.connections?.find(row => row.connection_id === connection.connection_id)
    if (!data || typeof data.enabled !== 'boolean' || typeof saved?.enabled !== 'boolean') throw new Error('密钥开关保存结果不完整，请刷新确认')
    settings.value = data
    clearOneTimeToken()
    if (saved.enabled !== enabled) throw new Error('密钥开关状态未保存成功，请刷新后重试')
    message.success(enabled ? (data.enabled ? '密钥已开启' : '密钥已开启，总开关关闭期间暂停使用') : '密钥已关闭')
  } catch (error) { if (isCurrent(current)) actionError.value = errorText(error) }
  finally { if (isCurrent(current)) togglingId.value = null }
}

const handleRevoke = async connection => {
  if (busy.value || connectionStatus(connection).label === '已撤销') return
  const current = session
  revokingId.value = connection.connection_id
  actionError.value = ''
  try {
    if (!await message.confirm(`撤销连接“${connection.name}”后，该密钥立即失效。确定撤销？`)) return
    if (!isCurrent(current)) return
    await api.mcpAccess.revokeConnection(connection.connection_id)
    if (!isCurrent(current)) return
    clearOneTimeToken()
    const data = await api.mcpAccess.settings()
    if (!isCurrent(current)) return
    settings.value = data
    message.success('连接已撤销')
  } catch (error) { if (isCurrent(current)) actionError.value = errorText(error) }
  finally { if (isCurrent(current)) revokingId.value = null }
}

const copyText = async text => {
  if (!text) return
  try {
    await navigator.clipboard.writeText(text)
    message.success('已复制，请妥善保管')
  } catch (_) { message.error('复制失败，请手动复制；浏览器需要安全连接和剪贴板权限') }
}
const changeLogPage = page => { logQuery.page = page; loadLogs() }
const resetSession = () => {
  ++session
  ++logRequest
  clearOneTimeToken()
  settings.value = null
  logs.value = []
  logsTotal.value = 0
  loading.value = false
  saving.value = false
  creating.value = false
  revokingId.value = null
  togglingId.value = null
  form.name = ''
  form.mode = 'read_write'
  logQuery.page = 1
}
const handleClose = () => { resetSession(); emit('update:modelValue', false) }
watch(() => props.modelValue, visible => { if (visible) loadSettings(); else resetSession() }, { immediate: true })
onBeforeUnmount(resetSession)
</script>

<style scoped lang="scss">
.mcp-access { padding: 0 4px 24px; color: var(--el-text-color-primary); }
.mcp-state { padding: 30px 12px; min-height: 260px; }
.mcp-card { margin-top: 16px; padding: 20px; background: var(--el-bg-color); border: 1px solid var(--el-border-color-lighter); border-radius: 6px; }
.mcp-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 16px; }
h3 { margin: 0 0 14px; font-size: 16px; font-weight: 600; }
.mcp-heading h3 { margin: 0; }
h4 { margin: 0 0 10px; }
.mcp-note { color: var(--el-text-color-secondary); font-size: 13px; line-height: 1.7; margin: 12px 0; }
.mcp-copy-row { display: flex; align-items: center; gap: 12px; min-width: 0; }
.mcp-copy-row code { overflow-wrap: anywhere; flex: 1; }
.mcp-copy-row .el-input { flex: 1; min-width: 0; }
.mcp-row-actions { display: flex; align-items: center; gap: 12px; }
.mcp-row-actions .el-button + .el-button { margin-left: 0; }
.mcp-secret { padding: 16px; border: 1px solid var(--el-color-warning-light-5); background: var(--el-color-warning-light-9); border-radius: 6px; margin: 16px 0; }
.mcp-secret p { font-size: 13px; line-height: 1.7; }
.mcp-secret-actions { display: flex; flex-wrap: wrap; gap: 8px; }
pre { background: var(--el-fill-color-light); padding: 14px; font-size: 12px; line-height: 1.6; white-space: pre-wrap; overflow-wrap: anywhere; border-radius: 4px; user-select: text; }
.mcp-table, .mcp-retry, .mcp-pagination { margin-top: 14px; }
.mcp-pagination { justify-content: flex-end; }
@media (max-width: 800px) { .mcp-card { padding: 14px; } }
</style>
